import React, { HTMLAttributes, ReactNode, useState } from 'react';

import {
  TextField,
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderOptionState,
  TextFieldProps,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { VirtualizedList } from '../VirtualizedList/VirtualizedList';

type SearchInputProps<TOption> = {
  options: TOption[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onSelectOption: (option: TOption | null) => void;
  renderOptionLabel?: (option: TOption) => ReactNode;
} & Pick<
  AutocompleteProps<TOption, false, false, false>,
  | 'loading'
  | 'noOptionsText'
  | 'className'
  | 'getOptionLabel'
  | 'getOptionKey'
  | 'isOptionEqualToValue'
> &
  Pick<TextFieldProps, 'variant' | 'size' | 'placeholder'>;

const SearchInput = <TOption extends Record<string, string | number>>({
  options,
  inputValue,
  onInputChange,
  onSelectOption,
  getOptionLabel = () => '',
  isOptionEqualToValue,
  getOptionKey,
  renderOptionLabel,
  loading,
  noOptionsText,
  className,
  variant,
  size,
  placeholder,
}: SearchInputProps<TOption>) => {
  const [open, setOpen] = useState(false);

  const getRenderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: TOption,
    state: AutocompleteRenderOptionState,
  ) =>
    ({
      props,
      label: renderOptionLabel ? renderOptionLabel(option) : getOptionLabel(option),
      selected: state.selected,
    }) as any;

  const renderInput = (params: TextFieldProps) => (
    <TextField {...params} placeholder={placeholder} variant={variant} size={size} />
  );

  return (
    <Autocomplete
      fullWidth
      open={open && inputValue.length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      popupIcon={<SearchRoundedIcon fontSize="large" color="disabled" />}
      inputValue={inputValue}
      onInputChange={(_, value) => {
        onInputChange(value);
      }}
      onChange={(_, option) => {
        onSelectOption(option);
      }}
      size={size}
      loading={loading}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionKey={getOptionKey}
      renderOption={getRenderOption}
      filterOptions={(availableOptions) => availableOptions}
      noOptionsText={noOptionsText}
      className={className}
      slotProps={{
        popper: { className: 'general-style variant-select', placement: 'bottom-start' },
        popupIndicator: {
          sx: { transform: 'none !important' },
        },
        listbox: { component: VirtualizedList as any },
      }}
      renderInput={renderInput}
    />
  );
};

export { SearchInput };
